// hooks/useEndInterview.js
import { useState, useCallback } from 'react';
import { toast } from '../../../components/Common/Toast';

const useEndInterview = (
  token,
  selectedTeam,
  fetchTeamMembers,
  setActiveInterview,
  setIsInterviewActive,
  setTeams,
  setSelectedTeam,
  setShowGroupVideoCall,
  setIsGroupCallMinimized,
  activeInterviewIdRef,
  clearInterviewTimers
) => {
  const [isEnding, setIsEnding] = useState(false);
  const [showEndInterviewModal, setShowEndInterviewModal] = useState(false);
  const [endInterviewId, setEndInterviewId] = useState(null);

  const handleEndInterview = useCallback(() => {
    const interviewId = selectedTeam?.active_interview?.interview_id || activeInterviewIdRef.current;
    
    if (!interviewId) {
      toast.error('No active interview found');
      return;
    }

    setShowEndInterviewModal(true);
  }, [selectedTeam, activeInterviewIdRef]);

  const handleCandidateAction = useCallback((candidateId, action) => {
    if (action === 'selected') {
      if (selectedTeam?.team_id) {
        fetchTeamMembers(selectedTeam.team_id);
      }
    }
  }, [selectedTeam, fetchTeamMembers]);

  // Called from InterviewVideoCall when meeting ends
  const handleMeetingEndedCleanup = useCallback(async () => {
    const interviewId = selectedTeam?.active_interview?.interview_id || activeInterviewIdRef.current;
    
    // Close video call UI
    setShowGroupVideoCall(false);
    setIsGroupCallMinimized(false);
    
    // Clear refs
    if (activeInterviewIdRef) {
      activeInterviewIdRef.current = null;
    }
    
    if (clearInterviewTimers) {
      clearInterviewTimers();
    }
    
    // Refresh team data
    if (selectedTeam?.team_id) {
      await fetchTeamMembers(selectedTeam.team_id);
    }
    
    // Show the review modal for admin
    setEndInterviewId(interviewId);
    setShowEndInterviewModal(true);
    toast.success('Interview ended. Review candidates below.');
  }, [selectedTeam, activeInterviewIdRef, clearInterviewTimers, fetchTeamMembers, setShowGroupVideoCall, setIsGroupCallMinimized]);

  const handleCloseEndInterviewModal = useCallback(() => {
    setShowEndInterviewModal(false);
    
    // Clean up interview state after modal closes
    setActiveInterview(null);
    setIsInterviewActive(false);
    
    // Update teams list
    setTeams(prevTeams => prevTeams.map(team => 
      team.team_id === selectedTeam?.team_id 
        ? { ...team, active_interview: null }
        : team
    ));
    
    if (setSelectedTeam) {
      setSelectedTeam(prev => prev ? { ...prev, active_interview: null } : prev);
    }
    
    setEndInterviewId(null);
  }, [setActiveInterview, setIsInterviewActive, setTeams, setSelectedTeam, selectedTeam]);

  return {
    handleEndInterview,
    showEndInterviewModal,
    setShowEndInterviewModal: handleCloseEndInterviewModal,
    handleCandidateAction,
    handleMeetingEndedCleanup,
    endInterviewId,
    isEnding
  };
};

export default useEndInterview;